import { AxiosInstance } from 'axios'
import { useMutation, useQueryClient } from 'react-query'
import { useAxiosContext } from '@dc-shared-web/providers/AxiosProvider'
import { useToast } from '@dc-shared-web/components/shadcn/use-toast'
import {
  LIST_QUERY_KEY,
  DOCUMENTS_QUERY_KEY,
} from '@dc-shared-web/hooks/api/queryKeys'
import {
  CrunchesPageEntities,
  shouldInvalidateCrunchesPage,
} from '@dc-shared-web/hooks/utils/shouldInvalidateCrunchesPage'

type UpdateDocumentStateParams = {
  state: 'archive' | 'unarchive' | 'delete' | 'restore'
  uuid: string
  name?: string
}

const updateDocumentState = async (
  apiClient: AxiosInstance,
  params: UpdateDocumentStateParams
) => {
  const { data } = await apiClient.postForm(
    `/ajax/crunches/${params.uuid}/state/${params.state}`,
    {
      uuid: params.uuid,
      state: params.state,
    }
  )
  return data
}

export const useUpdateDocumentState = (params: {
  documentId: string
  onSuccess?: () => void
}) => {
  const apiClient = useAxiosContext()
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation(
    (mutationParams: UpdateDocumentStateParams) =>
      updateDocumentState(apiClient, mutationParams),
    {
      onSuccess: async (_, successParams) => {
        await queryClient.invalidateQueries<CrunchesPageEntities>({
          queryKey: [DOCUMENTS_QUERY_KEY, LIST_QUERY_KEY],
          refetchPage: (page) => {
            return shouldInvalidateCrunchesPage(page, params.documentId)
          },
        })

        toast({
          variant: 'success',
          title:
            successParams.state === 'archive' ||
            successParams.state === 'unarchive'
              ? `${successParams.name} has been ${successParams.state}d.`
              : `Successfully ${successParams.state}d document.`,
        })
        params?.onSuccess?.()
      },
      onError: (_, errorParams) => {
        toast({
          variant: 'error',
          title: `Error ${errorParams.state.slice(0, -1)}ing document.`,
          description: 'If the issue persists, please contact support.',
        })
      },
    }
  )
}
